import React from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeading } from '../components/ui/SectionHeading';
import { StepList } from '../components/StepList';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Accessibility as AccessibilityIcon, ArrowRight, Users, ShieldCheck, CheckCircle2, Search, ClipboardList, MessageCircle, Wrench } from 'lucide-react';
const ACCESSIBILITE_STEPS = [{
  title: 'Identification des besoins',
  description: "Échange confidentiel avec le référent handicap avant ou lors de l'inscription pour évaluer les besoins spécifiques.",
  icon: <Search className="w-6 h-6" data-id="element-588" />
}, {
  title: "Plan d'accompagnement individualisé",
  description: 'Définition des aménagements pédagogiques, techniques ou organisationnels nécessaires.',
  icon: <ClipboardList className="w-6 h-6" data-id="element-589" />
}, {
  title: 'Réunion de concertation',
  description: "Implication de l'équipe pédagogique et, si besoin, de l'entreprise d'accueil pour assurer la cohérence du parcours.",
  icon: <MessageCircle className="w-6 h-6" data-id="element-590" />
}, {
  title: 'Déploiement des aménagements',
  description: 'Mise en place effective des solutions (tiers temps, matériel adapté, supports spécifiques, etc.).',
  icon: <Wrench className="w-6 h-6" data-id="element-591" />
}];
export function Accessibilite() {
  return <div className="flex flex-col w-full" data-id="element-592">
      <PageHeader title="Accessibilité et Handicap" subtitle="Zillenium Alternance s'engage pour un accueil inclusif et la réussite de tous les apprenants." data-id="element-593" />

      <div className="py-16 md:py-24 bg-white relative overflow-hidden" data-id="element-594">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-brand-orange rounded-full blur-[100px] opacity-10 pointer-events-none" data-id="element-595" />
        <div className="container max-w-5xl mx-auto relative z-10" data-id="element-596">
          {/* Engagement */}
          <section className="flex flex-col md:flex-row gap-10 items-center bg-white shadow-xl p-8 md:p-12 rounded-3xl mb-24 border-none group hover:-translate-y-2 transition-transform duration-300" data-id="element-597">
            <div className="w-24 h-24 bg-lavande rounded-3xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300" data-id="element-598">
              <AccessibilityIcon className="w-12 h-12 text-brand-orange animate-soft-bounce" data-id="element-599" />
            </div>
            <div data-id="element-600">
              <h2 className="text-3xl font-extrabold font-heading text-primary-dark mb-4" data-id="element-601">
                Notre engagement d'accueil inclusif
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed" data-id="element-602">
                Nous mettons tout en œuvre pour rendre nos formations
                accessibles aux personnes en situation de handicap. Notre
                objectif est de garantir l'égalité des chances en adaptant nos
                parcours de formation et nos modalités d'évaluation aux besoins
                spécifiques de chacun.
              </p>
            </div>
          </section>

          {/* Processus */}
          <section className="mb-24" data-id="element-603">
            <SectionHeading title="Le processus d'accompagnement" className="mb-12" data-id="element-604" />
            <StepList steps={ACCESSIBILITE_STEPS} data-id="element-605" />
          </section>

          <div className="grid md:grid-cols-2 gap-10 mb-24" data-id="element-606">
            {/* Suivi */}
            <Card className="border-none shadow-xl bg-white hover:-translate-y-2 transition-transform duration-300 group" data-id="element-607">
              <div className="h-2 w-full bg-primary" data-id="element-608" />
              <CardContent className="p-8" data-id="element-609">
                <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-6 flex items-center gap-4" data-id="element-610">
                  <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-611">
                    <CheckCircle2 className="w-6 h-6 text-primary animate-soft-pulse" data-id="element-612" />
                  </div>
                  Suivi du parcours
                </h3>
                <p className="text-lg text-muted-foreground mb-8 leading-relaxed" data-id="element-613">
                  Un suivi régulier est assuré tout au long de la formation avec
                  3 rendez-vous annuels dédiés :
                </p>
                <ul className="space-y-4" data-id="element-614">
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-615">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" data-id="element-616"></div>
                    <span className="font-bold text-primary-dark" data-id="element-617">
                      En début de parcours
                    </span>
                  </li>
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-618">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" style={{
                    animationDelay: '0.5s'
                  }} data-id="element-619"></div>
                    <span className="font-bold text-primary-dark" data-id="element-620">
                      À mi-parcours
                    </span>
                  </li>
                  <li className="flex items-center gap-4 bg-muted p-4 rounded-xl" data-id="element-621">
                    <div className="w-3 h-3 rounded-full bg-brand-orange animate-soft-pulse" style={{
                    animationDelay: '1s'
                  }} data-id="element-622"></div>
                    <span className="font-bold text-primary-dark" data-id="element-623">
                      En fin de parcours
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Contacts & Partenaires */}
            <div className="space-y-10" data-id="element-624">
              <Card className="bg-primary-dark text-white border-none shadow-xl hover:-translate-y-2 transition-transform duration-300 group" data-id="element-625">
                <div className="h-2 w-full bg-primary-accent" data-id="element-626" />
                <CardContent className="p-8" data-id="element-627">
                  <h3 className="text-2xl font-extrabold font-heading mb-6 flex items-center gap-4" data-id="element-628">
                    <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center group-hover:scale-110 transition-transform" data-id="element-629">
                      <Users className="w-6 h-6 text-lavande animate-soft-float" data-id="element-630" />
                    </div>
                    Vos référents handicap
                  </h3>
                  <p className="text-lg text-lavande mb-6 leading-relaxed" data-id="element-631">
                    Nos référents sont à votre écoute pour construire avec vous
                    votre parcours de formation.
                  </p>
                  <div className="space-y-3 mb-8" data-id="element-632">
                    <div className="font-bold text-xl" data-id="element-633">Samir MOKEDDEM</div>
                    <div className="font-bold text-xl" data-id="element-634">Fadma JIBJIB</div>
                  </div>
                  <a href="mailto:contact@zilleniumalternance.fr" className="text-brand-orange font-bold hover:text-white transition-colors underline underline-offset-4 text-lg" data-id="element-635">
                    Contacter les référents
                  </a>
                </CardContent>
              </Card>

              <div className="bg-white p-8 rounded-3xl shadow-lg border-none" data-id="element-636">
                <h3 className="font-extrabold font-heading text-xl text-primary-dark mb-4" data-id="element-637">
                  Nos partenaires relais
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed" data-id="element-638">
                  Nous travaillons en étroite collaboration avec les acteurs
                  spécialisés du territoire :
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-639">
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-640">
                    AGEFIPH BFC
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-641">
                    Mission Locale
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-642">
                    Cap Emploi
                  </span>
                  <span className="text-sm font-bold bg-lavande text-primary-dark px-3 py-1.5 rounded-lg" data-id="element-643">
                    MDPH
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Droits & RGPD */}
          <section className="space-y-10 bg-muted p-8 md:p-12 rounded-3xl" data-id="element-644">
            <div data-id="element-645">
              <h3 className="text-2xl font-extrabold font-heading text-primary-dark mb-6" data-id="element-646">
                Les droits de l'apprenant
              </h3>
              <ul className="list-disc pl-6 space-y-3 text-lg text-muted-foreground marker:text-brand-orange" data-id="element-647">
                <li data-id="element-648">Droit à la non-discrimination à l'accès à la formation.</li>
                <li data-id="element-649">
                  Droit à l'aménagement des épreuves d'examen (tiers temps,
                  assistance, etc.).
                </li>
                <li data-id="element-650">Droit à l'adaptation du poste de travail en entreprise.</li>
                <li data-id="element-651">Droit à la confidentialité des informations médicales.</li>
              </ul>
            </div>

            <div className="flex gap-6 items-start bg-white p-8 rounded-2xl shadow-sm border-none group" data-id="element-652">
              <div className="w-12 h-12 rounded-xl bg-lavande flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" data-id="element-653">
                <ShieldCheck className="w-6 h-6 text-primary animate-soft-pulse" data-id="element-654" />
              </div>
              <p className="text-muted-foreground leading-relaxed" data-id="element-655">
                <strong className="text-primary-dark font-bold text-lg block mb-2" data-id="element-656">
                  Confidentialité (RGPD)
                </strong>
                Les informations relatives à votre situation de handicap sont
                strictement confidentielles. Elles ne sont partagées avec
                l'équipe pédagogique ou l'entreprise qu'avec votre accord
                explicite, et uniquement dans le but de mettre en place les
                aménagements nécessaires.
              </p>
            </div>

            <div className="text-center pt-8" data-id="element-657">
              <Button size="lg" className="group bg-brand-orange hover:bg-[#E67E22] text-white text-lg px-8 py-7 shadow-xl" asChild data-id="element-658">
                <Link to="/candidater" data-id="element-659">
                  Remplir le formulaire etudiant
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-660" />
                </Link>
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>;
}